import React from 'react';
import clsx from 'clsx';
import { Image } from 'src/js/model/rainbow/ImageOutput';
import times from 'lodash.times';
import {
  GenericCarousel,
  ClickCallback,
  IndicatorClickCallback,
} from 'js/components/GenericCarousel';
import { Chevron, Direction } from 'js/components/Icons/Chevron/Chevron';
import { trackImageSlider } from 'js/pages/BrowsePage/tracking/browse-result';
import { Category } from 'js/pages/BrowsePage/tracking/tracking';
import { LazyCarouselImage } from 'js/components/generic/LazyCarouselImage';
import styles from './BrowseResultImageCarousel.module.css';

interface Props {
  positioningClassName?: string;
  images: Image[];
  isInHybridView?: boolean;
  isLazy: boolean;
}

export class BrowseResultImageCarousel extends React.PureComponent<Props> {
  private renderItem = (image: Image, index: number): React.ReactNode => (
    <LazyCarouselImage
      className={styles.image}
      imageData={image}
      sizes="BROWSE_PAGE"
      isLazy={this.props.isLazy || index > 0}
    />
  );

  private renderChevrons(
    isShow: boolean,
    leftClickCallback: ClickCallback,
    rightClickCallback: ClickCallback
  ): React.ReactNode {
    return (
      <>
        <div
          className={clsx(
            styles.chevronContainer,
            styles.chevronContainerLeft,
            {
              [styles.chevronShow]: isShow,
            }
          )}
          onClick={leftClickCallback}
        >
          <Chevron colour="white" direction={Direction.Left} />
        </div>
        <div
          className={clsx(
            styles.chevronContainer,
            styles.chevronContainerRight,
            {
              [styles.chevronShow]: isShow,
            }
          )}
          onClick={rightClickCallback}
        >
          <Chevron colour="white" direction={Direction.Right} />
        </div>
      </>
    );
  }

  private renderPagination(
    numItems: number,
    activeIndex: number,
    onIndicatorClick: IndicatorClickCallback
  ): React.ReactNode {
    return (
      <div className={styles.pagination}>
        {times(numItems, (index: number) => (
          <div
            key={index}
            className={clsx(styles.indicator, {
              [styles.indicatorActive]: index === activeIndex,
            })}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              onIndicatorClick(index);
            }}
          />
        ))}
      </div>
    );
  }

  private renderNavigation = (
    numItems: number,
    activeIndex: number,
    itemNavigate: IndicatorClickCallback,
    isHover: boolean,
    prevItemNavigate: ClickCallback,
    nextItemNavigate: ClickCallback
  ): React.ReactNode => (
    <>
      {this.renderPagination(numItems, activeIndex, itemNavigate)}
      {this.renderChevrons(isHover, prevItemNavigate, nextItemNavigate)}
    </>
  );

  private onItemChanged = (itemIndex: number): void => {
    const category = this.props.isInHybridView
      ? Category.BrowseHybridCard
      : Category.BrowseListCard;
    trackImageSlider(
      category,
      (itemIndex + 1).toString(),
      this.props.images.length.toString()
    );
  };

  public render(): React.ReactNode {
    return (
      <GenericCarousel
        positioningClassName={this.props.positioningClassName}
        items={this.props.images}
        renderItem={this.renderItem}
        renderNavigation={this.renderNavigation}
        onItemChanged={this.onItemChanged}
      />
    );
  }
}
